import { WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import * as echarts from "echarts/core";
import { SankeyChart, ScatterChart, EffectScatterChart, LinesChart } from "echarts/charts";
import { GeoComponent, TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { Select, Skeleton } from "@nexus/ui";
import {
  defineLabels,
  firstError,
  resolveChartColors,
  resolveLabels,
  unwrap,
  UpdatingPill,
  Widget,
  withAlpha,
  HeaderStats,
} from "@nexus/widget-sdk";
import type { ChartColors, WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { prefersReducedMotion } from "@nexus/widget-kit";
import { FLOW_GEO_MAP_CONTRACT } from "./types";
import type { FlowGeoMapData, FlowLink, FlowNode } from "./types";

echarts.use([
  SankeyChart,
  ScatterChart,
  EffectScatterChart,
  LinesChart,
  GeoComponent,
  TooltipComponent,
  CanvasRenderer,
]);

export interface FlowGeoMapConfig {
  title?: string;
  labels?: Record<string, string>;
}

export const LABELS = defineLabels([
  { key: "stageSource", name: "Stage: source", default: "Source" },
  { key: "stageMidpoint", name: "Stage: midpoint", default: "Midpoint" },
  { key: "stageSink", name: "Stage: sink", default: "Sink" },
  { key: "flowName", name: "Flow name", default: "records" },
  { key: "focusLabel", name: "Highlight selector", default: "Highlight" },
  { key: "focusNone", name: "Highlight selector: no focus", default: "Everything", advanced: true },
  { key: "emptyTitle", name: "Empty title", default: "No data" },
  {
    key: "emptyDescription",
    name: "Empty description",
    default: "Nothing was reported for this period.",
    advanced: true,
  },
  { key: "mapCaption", name: "Map caption", default: "Geographic distribution", advanced: true },
] as const);

type FlowLabels = ReturnType<typeof resolveLabels<typeof LABELS>>;

export const manifest: WidgetManifest = {
  id: "flow-geo-map",
  name: "Flow + geo map",
  description:
    "A three-stage flow graph correlated with a world map of its endpoints, with hover/click highlighting shared between both.",
  version: "1.0.0",
  labels: LABELS,
  dataRequests: {
    graph: {
      source: "flow-geo-map",
      contract: FLOW_GEO_MAP_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: FlowGeoMapConfig = {};

type Selection = { kind: "source" | "sink"; id: string } | null;

let worldMapRegistered = false;
let worldMapRegistering: Promise<void> | null = null;

function ensureWorldMap(): Promise<void> {
  if (worldMapRegistered) return Promise.resolve();
  if (!worldMapRegistering) {
    worldMapRegistering = import("./world.geo.json").then((module) => {
      echarts.registerMap("world", module.default as never);
      worldMapRegistered = true;
    });
  }
  return worldMapRegistering;
}

function litSets(links: FlowLink[], selection: Selection) {
  const litNodes = new Set<string>();
  const litLinks = new Set<FlowLink>();
  if (!selection) return { litNodes, litLinks, active: false };
  if (selection.kind === "source") {
    const mids = new Set(links.filter((l) => l.source === selection.id).map((l) => l.target));
    const sinks = new Set(links.filter((l) => mids.has(l.source)).map((l) => l.target));
    litNodes.add(selection.id);
    mids.forEach((m) => litNodes.add(m));
    sinks.forEach((s) => litNodes.add(s));
    links.forEach((l) => {
      if (l.source === selection.id || mids.has(l.source)) litLinks.add(l);
    });
  } else {
    const mids = new Set(links.filter((l) => l.target === selection.id).map((l) => l.source));
    const sources = new Set(links.filter((l) => mids.has(l.target)).map((l) => l.source));
    litNodes.add(selection.id);
    mids.forEach((m) => litNodes.add(m));
    sources.forEach((s) => litNodes.add(s));
    links.forEach((l) => {
      if (l.target === selection.id || (mids.has(l.target) && sources.has(l.source)))
        litLinks.add(l);
    });
  }
  return { litNodes, litLinks, active: true };
}

function stageLabel(kind: FlowNode["kind"], labels: FlowLabels): string {
  return kind === "source"
    ? labels.stageSource
    : kind === "sink"
      ? labels.stageSink
      : labels.stageMidpoint;
}

function nodeColor(colors: ChartColors, kind: FlowNode["kind"]): string {
  return kind === "sink"
    ? colors.series[3]
    : kind === "source"
      ? colors.series[0]
      : colors.series[1];
}

function buildSankeyOption(
  data: FlowGeoMapData,
  colors: ChartColors,
  selection: Selection,
  labels: FlowLabels,
): echarts.ComposeOption<never> {
  const { litNodes, litLinks, active } = litSets(data.links, selection);
  const kindByName = new Map(data.nodes.map((n) => [n.id, n.kind]));
  return {
    tooltip: {
      trigger: "item",
      triggerOn: "mousemove",
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      textStyle: { color: colors.tooltipText, fontSize: colors.typography?.labelSize ?? 12 },
      formatter: (params: { dataType?: string; name?: string; value?: number }) => {
        if (params.dataType === "edge") return "";
        const kind = kindByName.get(params.name ?? "");
        return kind
          ? `<strong>${params.name}</strong><br>${stageLabel(kind, labels)}`
          : String(params.name ?? "");
      },
    },
    series: [
      {
        type: "sankey",
        left: 8,
        right: 8,
        top: 12,
        bottom: 12,
        nodeGap: 8,
        nodeWidth: 14,
        layoutIterations: 0,
        nodeAlign: "justify",
        emphasis: { focus: "adjacency" },
        data: data.nodes.map((node) => {
          const base = nodeColor(colors, node.kind);
          const dimmed = active && !litNodes.has(node.id);
          return {
            name: node.id,
            label: { position: node.kind === "sink" ? "left" : "right", formatter: node.label },
            itemStyle: { color: dimmed ? withAlpha(base, 0.2) : base, borderWidth: 0 },
          };
        }),
        links: data.links.map((link) => ({
          ...link,
          lineStyle: { opacity: active ? (litLinks.has(link) ? 0.5 : 0.06) : 0.42 },
        })),
        lineStyle: { color: "gradient", opacity: 0.42, curveness: 0.5 },
        label: { color: colors.tooltipText, fontSize: colors.typography?.labelSize ?? 12 },
        itemStyle: { borderWidth: 0 },
      },
    ],
    backgroundColor: "transparent",
  } as echarts.ComposeOption<never>;
}

function buildMapOption(
  data: FlowGeoMapData,
  colors: ChartColors,
  selection: Selection,
  reduceMotion: boolean,
): echarts.ComposeOption<never> {
  const isSource = selection?.kind === "source";
  const isSink = selection?.kind === "sink";
  const anchorCoords = new Map(
    data.anchors.map((a) => [a.nodeId, [a.lng, a.lat] as [number, number]]),
  );

  const baseDots = isSink ? [] : data.markers;
  const highlighted = isSource
    ? data.markers.filter((m) => m.sourceNodeId === selection.id)
    : isSink
      ? data.markers.filter((m) => m.sinkNodeId === selection.id)
      : [];
  const shownAnchors = isSink
    ? data.anchors.filter((a) => a.nodeId === selection.id)
    : data.anchors;

  const flowLines = isSink
    ? highlighted
        .map((m) => {
          const from = anchorCoords.get(m.sinkNodeId);
          return from ? { coords: [from, [m.lng, m.lat]] } : null;
        })
        .filter((l): l is { coords: number[][] } => l !== null)
    : isSource
      ? highlighted
          .map((m) => {
            const to = anchorCoords.get(m.sinkNodeId);
            return to ? { coords: [[m.lng, m.lat], to] } : null;
          })
          .filter((l): l is { coords: number[][] } => l !== null)
      : [];

  return {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      textStyle: { color: colors.tooltipText, fontSize: colors.typography?.labelSize ?? 12 },
    },
    geo: {
      map: "world",
      roam: true,
      zoom: 1.1,
      itemStyle: {
        areaColor: withAlpha(colors.label, 0.1),
        borderColor: colors.axisLine,
        borderWidth: 0.5,
      },
      emphasis: {
        itemStyle: { areaColor: withAlpha(colors.accent, 0.18) },
        label: { show: false },
      },
    },
    series: [
      {
        name: "Points",
        type: "scatter",
        coordinateSystem: "geo",
        zlevel: 1,
        symbolSize: 4,
        itemStyle: { color: withAlpha(colors.series[0], isSource ? 0.15 : 0.5) },
        data: baseDots.map((m) => ({ name: m.sourceNodeId, value: [m.lng, m.lat] })),
      },
      {
        name: "Selected points",
        type: reduceMotion ? "scatter" : "effectScatter",
        coordinateSystem: "geo",
        zlevel: 3,
        symbolSize: 8,
        rippleEffect: { scale: 2.5, brushType: "stroke" },
        itemStyle: { color: colors.series[0] },
        data: highlighted.map((m) => ({
          name: `${m.sourceNodeId} · ${m.sinkNodeId}`,
          value: [m.lng, m.lat],
        })),
      },
      {
        name: "Flow",
        type: "lines",
        coordinateSystem: "geo",
        zlevel: 2,
        effect: { show: !reduceMotion, period: 4, trailLength: 0.3, symbolSize: 3 },
        lineStyle: { color: colors.series[3], width: 1, opacity: 0.6, curveness: 0.3 },
        data: flowLines,
      },
      {
        name: "Anchor",
        type: "scatter",
        coordinateSystem: "geo",
        zlevel: 4,
        symbol: "diamond",
        symbolSize: 12,
        itemStyle: { color: colors.series[3], borderColor: colors.tooltipBg, borderWidth: 1 },
        data: shownAnchors.map((a) => ({ name: a.nodeId, value: [a.lng, a.lat] })),
      },
    ],
  } as echarts.ComposeOption<never>;
}

function FlowGeoMapComponent({ data, config }: WidgetProps<FlowGeoMapConfig>) {
  const error = firstError(data, ["graph"]);
  const source = unwrap<FlowGeoMapData>(data.graph);
  const isLoading = data.graph?.isLoading ?? false;
  const title = config.title?.trim() || defaultConfig.title || manifest.name;
  const labelsJson = JSON.stringify(resolveLabels(LABELS, config));
  const labels = useMemo(() => JSON.parse(labelsJson) as FlowLabels, [labelsJson]);

  const [selection, setSelection] = useState<Selection>(null);
  const [hoverSelection, setHoverSelection] = useState<Selection>(null);
  const [mapReady, setMapReady] = useState(worldMapRegistered);
  const [colors, setColors] = useState<ChartColors | null>(null);
  const active = hoverSelection ?? selection;

  const sankeyRef = useRef<HTMLDivElement | null>(null);
  const sankeyInstanceRef = useRef<echarts.ECharts | null>(null);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<echarts.ECharts | null>(null);

  // ECharts cannot lay out a chart (a geo map in particular) in a zero-sized
  // host, e.g. while its collapsible widget starts collapsed: charts are only
  // created once their host is actually displayed.
  const hasSource = Boolean(source);
  const [sankeyShown, setSankeyShown] = useState(false);
  const [mapShown, setMapShown] = useState(false);

  const reduceMotion = prefersReducedMotion();

  useEffect(() => {
    let cancelled = false;
    void ensureWorldMap().then(() => {
      if (!cancelled) setMapReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Sankey chart lifecycle ──────────────────────────────────────────────
  useEffect(() => {
    const host = sankeyRef.current;
    if (!host || !source || !sankeyShown) return;
    if (!sankeyInstanceRef.current || sankeyInstanceRef.current.isDisposed()) {
      const chart = echarts.init(host, null, { renderer: "canvas" });
      chart.on("mouseover", (params: unknown) => {
        const p = params as { dataType?: string; name?: string };
        if (p.dataType !== "node" || !p.name) return;
        const kind = source.nodes.find((n) => n.id === p.name)?.kind;
        if (kind === "source" || kind === "sink") setHoverSelection({ kind, id: p.name });
      });
      chart.on("mouseout", () => setHoverSelection(null));
      chart.on("click", (params: unknown) => {
        const p = params as { dataType?: string; name?: string };
        if (p.dataType !== "node" || !p.name) return;
        const id = p.name;
        const kind = source.nodes.find((n) => n.id === id)?.kind;
        if (kind !== "source" && kind !== "sink") return;
        setSelection((current) =>
          current?.kind === kind && current.id === id ? null : { kind, id },
        );
      });
      sankeyInstanceRef.current = chart;
    }
    const resolved = resolveChartColors(host);
    setColors(resolved);
    sankeyInstanceRef.current.setOption(buildSankeyOption(source, resolved, active, labels), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, active, labels, sankeyShown]);

  useEffect(() => {
    const host = sankeyRef.current;
    if (!host) return;
    const sizeObserver = new ResizeObserver(() => {
      setSankeyShown(host.clientWidth > 0 && host.clientHeight > 0);
      sankeyInstanceRef.current?.resize();
    });
    sizeObserver.observe(host);
    return () => {
      sizeObserver.disconnect();
      sankeyInstanceRef.current?.dispose();
      sankeyInstanceRef.current = null;
    };
    // The host only exists once data has arrived.
  }, [hasSource]);

  // ── Map chart lifecycle ──────────────────────────────────────────────────
  useEffect(() => {
    const host = mapRef.current;
    if (!host || !source || !mapReady || !mapShown) return;
    if (!mapInstanceRef.current || mapInstanceRef.current.isDisposed()) {
      mapInstanceRef.current = echarts.init(host, null, { renderer: "canvas" });
    }
    const resolved = colors ?? resolveChartColors(host);
    mapInstanceRef.current.setOption(buildMapOption(source, resolved, active, reduceMotion), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, active, mapReady, colors, reduceMotion, mapShown]);

  useEffect(() => {
    const host = mapRef.current;
    if (!host) return;
    const sizeObserver = new ResizeObserver(() => {
      setMapShown(host.clientWidth > 0 && host.clientHeight > 0);
      mapInstanceRef.current?.resize();
    });
    sizeObserver.observe(host);
    return () => {
      sizeObserver.disconnect();
      mapInstanceRef.current?.dispose();
      mapInstanceRef.current = null;
    };
    // The host only exists once data and the world map have arrived.
  }, [hasSource, mapReady]);

  // ── Theme changes: refresh both charts together ─────────────────────────
  useEffect(() => {
    const host = sankeyRef.current;
    if (!host) return;
    const themeObserver = new MutationObserver(() => {
      if (!sankeyInstanceRef.current || sankeyInstanceRef.current.isDisposed()) return;
      const resolved = resolveChartColors(host);
      setColors(resolved);
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => themeObserver.disconnect();
  }, [hasSource]);

  const selectionOptions = useMemo(
    () =>
      (source?.nodes ?? [])
        .filter((node) => node.kind === "source" || node.kind === "sink")
        .map((node) => ({ value: `${node.kind}:${node.id}`, label: node.label })),
    [source],
  );
  const selectionValue = selection ? `${selection.kind}:${selection.id}` : "";
  const selectedMarkers = active
    ? (source?.markers ?? []).filter((m) =>
        active.kind === "source" ? m.sourceNodeId === active.id : m.sinkNodeId === active.id,
      )
    : [];
  const selectedAnchorCount = new Set(selectedMarkers.map((m) => m.sinkNodeId)).size;

  if (error) {
    return (
      <Widget title={title}>
        <WidgetIssueState error={error} />
      </Widget>
    );
  }

  const isEmpty = !isLoading && (!source || source.nodes.length === 0);

  return (
    <Widget
      title={title}
      intrinsicHeight
      actions={isLoading && source ? <UpdatingPill /> : undefined}
      headerSummary={
        source?.summary?.length ? (
          <HeaderStats items={source.summary} label={`${title} summary`} />
        ) : undefined
      }
    >
      {isEmpty ? (
        <WidgetState title={labels.emptyDescription} />
      ) : !source ? (
        <WidgetSkeleton shape="chart" />
      ) : (
        <div className="flex w-full flex-1 flex-col gap-4 p-4">
          <div className="flex flex-wrap items-end gap-3 border-b border-border pb-4">
            <Select
              label={labels.focusLabel}
              value={selectionValue}
              emptyLabel={labels.focusNone}
              options={selectionOptions}
              onValueChange={(value) => {
                if (!value) {
                  setSelection(null);
                  return;
                }
                const separator = value.indexOf(":");
                const kind = value.slice(0, separator) === "source" ? "source" : "sink";
                setSelection({ kind, id: value.slice(separator + 1) });
              }}
              className="w-full @2xl/widget:w-80"
            />
            <p className="text-xs text-muted" aria-live="polite">
              {active
                ? `${active.id}: ${selectedMarkers.length} points across ${selectedAnchorCount} anchors`
                : `Showing every ${labels.stageSource.toLowerCase()} and ${labels.stageSink.toLowerCase()}`}
            </p>
          </div>
          <div className="grid flex-1 grid-cols-1 gap-6 @4xl/widget:grid-cols-2">
            <div
              ref={sankeyRef}
              role="img"
              aria-label="Flow graph"
              className="min-h-[24rem] w-full flex-1"
            />
            <div className="flex flex-col gap-2">
              <span className="text-2xs font-semibold uppercase tracking-widest text-muted">
                {labels.mapCaption}
              </span>
              {mapReady ? (
                <div
                  ref={mapRef}
                  role="img"
                  aria-label={labels.mapCaption}
                  className="min-h-[22rem] w-full flex-1"
                />
              ) : (
                <Skeleton className="min-h-[22rem] w-full rounded-lg" />
              )}
            </div>
          </div>
        </div>
      )}
    </Widget>
  );
}

export const Component = FlowGeoMapComponent;

const widgetModule: WidgetModule<FlowGeoMapConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
